// Xome MLS Fetcher — multi-broker per-state scrape with 7 data-backed tabs.
// Iterates ~36 broker portals (one per state) on auto-refresh every 8h.
// No agent column (Xome JSON exposes "courtesyOf" — kept in DB but not shown here).
import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { apiFetch } from '../helpers';

interface XomeRow {
  _id: string;
  xomeListingId?: string;
  brokerId?: string;
  address?: string;
  fullAddress?: string;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  listingPrice?: number | null;
  propertyTypeLabel?: string | null;
  listingTypeId?: string | null;
  beds?: number | null;
  baths?: number | null;
  sqft?: number | null;
  mlsStatus?: string | null;
  listDate?: string | null;
  courtesyOf?: string | null;
  detailUrl?: string | null;
  photoUrl?: string | null;
  pushStatus?: string | null;
  existingSource?: string | null;
  existingStage?: string | null;
  scrapedAt?: string;
  joinedAmv?: number | null;
  bofaAmv?: number | null;
  redfinAmv?: number | null;
  amvSource?: string | null;
  isDeal?: boolean;
  dealStage?: string | null;
  bofaStatus?: string | null;
  redfinTried?: boolean;
  redfinStatus?: string | null;
  pushedToSd?: boolean;
}

interface PerBrokerStat {
  state: string;
  brokerId: string;
  brokerStatus: string;
  fetched: number;
  pushed: number;
  durationMs: number;
  error: string | null;
}

interface ServiceStatus {
  isRunning: boolean;
  lastRunAt: string | null;
  lastRunDurationMs: number | null;
  lastError: string | null;
  lastErrorAt: string | null;
  lastCounts: {
    brokersAttempted: number;
    brokersSucceeded: number;
    brokersFailed: number;
    totalFetched: number;
    pushed: number;
    alreadyExists: number;
    skippedOverCap: number;
    skippedNoPrice: number;
    skippedNotSingleFamily: number;
    skippedStatus: number;
  };
  lastPerBrokerStats: PerBrokerStat[];
  configuredBrokers: number;
  activeBrokers?: number;
  staleBrokers?: number;
}

interface TabCounts {
  total: number;
  withAmv: number;
  bofaQueue: number;
  redfinQueue: number;
  withoutAmv: number;
  alreadyExists: number;
  skipped: number;
  deals: number;
}

interface StatsResponse {
  total: number;
  byState?: { _id: string; count: number }[];
  byBroker?: { _id: string; count: number }[];
}

type ViewMode = 'all' | 'with_amv' | 'bofa_queue' | 'redfin_queue' | 'without_amv' | 'already_exists' | 'skipped';

const currency = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmt = (n?: number | null) => (typeof n === 'number' && n > 0 ? currency.format(n) : '—');
const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '—');
const fmtDuration = (ms?: number | null) => (typeof ms === 'number' ? `${(ms / 1000).toFixed(1)}s` : '—');

export default function XomeFetcher() {
  const [status, setStatus] = useState<ServiceStatus | null>(null);
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [counts, setCounts] = useState<TabCounts>({ total: 0, withAmv: 0, bofaQueue: 0, redfinQueue: 0, withoutAmv: 0, alreadyExists: 0, skipped: 0, deals: 0 });
  const [rows, setRows] = useState<XomeRow[]>([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const ROWS_PER_PAGE = 50;
  const [viewMode, setViewMode] = useState<ViewMode>('all');
  const [filterState, setFilterState] = useState('all');
  const [rowsLoading, setRowsLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiFetch('/api/xome-scrape/status');
      setStatus(await res.json());
    } catch (e: any) { setError(e.message); }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await apiFetch('/api/xome-scrape/stats');
      setStats(await res.json());
    } catch {}
  }, []);

  const fetchCounts = useCallback(async () => {
    try {
      const res = await apiFetch('/api/xome-scrape/compare');
      const data = await res.json();
      if (data.ok) setCounts(data.counts);
    } catch {}
  }, []);

  const fetchRows = useCallback(async () => {
    setRowsLoading(true);
    try {
      const stateParam = filterState !== 'all' ? `&state=${filterState}` : '';
      const res = await apiFetch(`/api/xome-scrape/properties?viewMode=${viewMode}&page=${page}&limit=${ROWS_PER_PAGE}${stateParam}`);
      const data = await res.json();
      if (data.ok !== false) {
        setRows(data.properties || []);
        setRowsTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
      }
    } catch (e: any) { setError(e.message); }
    finally { setRowsLoading(false); }
  }, [viewMode, filterState, page]);

  useEffect(() => {
    fetchStatus(); fetchStats(); fetchCounts();
    const id = setInterval(() => { fetchStatus(); fetchStats(); fetchCounts(); }, 5000);
    return () => clearInterval(id);
  }, [fetchStatus, fetchStats, fetchCounts]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleDeleteAll = async () => {
    if (!window.confirm('Delete ALL Xome data (xome_properties + ScrapedDeal entries with source=xome)? This cannot be undone.')) return;
    setDeleting(true);
    try {
      const res = await apiFetch('/api/xome-scrape/delete-all', { method: 'POST' });
      const data = await res.json();
      if (data.ok) {
        alert(`Deleted ${data.deleted} Xome properties + ${data.xomeDealsDeleted || 0} pipeline entries.`);
        fetchStatus(); fetchStats(); fetchCounts(); fetchRows();
      }
    } catch (e: any) { alert('Failed: ' + (e?.message || 'Unknown error')); }
    finally { setDeleting(false); }
  };

  const isRunning = status?.isRunning || false;
  const brokerSummary = status ? `${status.lastCounts?.brokersSucceeded || 0}/${status.activeBrokers ?? status.configuredBrokers ?? 0} brokers ok` : '';

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#111' }}>
          Xome MLS
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          MLS listings from {status?.activeBrokers ?? status?.configuredBrokers ?? 33} active Xome broker portals
          {status?.staleBrokers ? ` (${status.staleBrokers} stale skipped)` : ''}. Auto-refreshes every 8 hours (3×/day).
          Filter: Single Family + Multi-Family, Active status only, $1–$500,000.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">Total</Typography>
          <Typography variant="h5" fontWeight={700}>{counts.total.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">Deals</Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#22c55e' }}>{counts.deals.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">With AMV</Typography>
          <Typography variant="h5" fontWeight={700} color="success.main">{counts.withAmv.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">BofA Queue</Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#2563eb' }}>{counts.bofaQueue.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">Redfin Queue</Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#dc2626' }}>{counts.redfinQueue.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">Without AMV</Typography>
          <Typography variant="h5" fontWeight={700} color="warning.main">{counts.withoutAmv.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">Already Exists</Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#6b7280' }}>{counts.alreadyExists.toLocaleString()}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">Skipped</Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#9ca3af' }}>{counts.skipped.toLocaleString()}</Typography>
        </Paper>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Button variant="outlined" color="error" startIcon={<DeleteForeverIcon />} onClick={handleDeleteAll} disabled={deleting || isRunning}>
            {deleting ? 'Deleting...' : 'Delete All'}
          </Button>
          <Box sx={{ ml: 'auto', textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary">
              Last refresh: {fmtDate(status?.lastRunAt)} {brokerSummary && `· ${brokerSummary}`}
            </Typography>
            {status?.lastRunDurationMs != null && (
              <Typography variant="caption" color="text.secondary">
                Duration: {fmtDuration(status.lastRunDurationMs)}
              </Typography>
            )}
          </Box>
        </Stack>

        {status?.lastError && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Last error ({fmtDate(status.lastErrorAt)}): {status.lastError}
          </Alert>
        )}
      </Paper>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
        <ToggleButtonGroup value={viewMode} exclusive onChange={(_, v) => { if (v) { setViewMode(v); setPage(1); } }} size="small">
          <ToggleButton value="all" sx={{ textTransform: 'none', fontWeight: 700 }}>
            All ({counts.total.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="with_amv" sx={{ textTransform: 'none', fontWeight: 600 }}>
            With AMV ({counts.withAmv.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="bofa_queue" sx={{ textTransform: 'none', fontWeight: 600 }}>
            BofA Queue ({counts.bofaQueue.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="redfin_queue" sx={{ textTransform: 'none', fontWeight: 600 }}>
            Redfin Queue ({counts.redfinQueue.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="without_amv" sx={{ textTransform: 'none', fontWeight: 600 }}>
            Without AMV ({counts.withoutAmv.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="already_exists" sx={{ textTransform: 'none', fontWeight: 600 }}>
            Already Exists ({counts.alreadyExists.toLocaleString()})
          </ToggleButton>
          <ToggleButton value="skipped" sx={{ textTransform: 'none', fontWeight: 600 }}>
            Skipped ({counts.skipped.toLocaleString()})
          </ToggleButton>
        </ToggleButtonGroup>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>State</InputLabel>
          <Select value={filterState} label="State" onChange={(e) => { setFilterState(e.target.value); setPage(1); }}>
            <MenuItem value="all">All States</MenuItem>
            {(stats?.byState || []).map((s) => (
              <MenuItem key={s._id} value={s._id}>{s._id} ({s.count.toLocaleString()})</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <div
        style={{
          overflowX: 'auto',
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          background: '#fff',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, color: '#111' }}>
            {viewMode === 'all' && `All Properties (${counts.total.toLocaleString()})`}
            {viewMode === 'with_amv' && `With AMV (${counts.withAmv.toLocaleString()})`}
            {viewMode === 'bofa_queue' && `BofA Queue (${counts.bofaQueue.toLocaleString()})`}
            {viewMode === 'redfin_queue' && `Redfin Queue (${counts.redfinQueue.toLocaleString()})`}
            {viewMode === 'without_amv' && `Without AMV (${counts.withoutAmv.toLocaleString()})`}
            {viewMode === 'already_exists' && `Already Exists (${counts.alreadyExists.toLocaleString()})`}
            {viewMode === 'skipped' && `Skipped (${counts.skipped.toLocaleString()})`}
          </span>
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            Showing {rows.length}{rowsTotal > rows.length ? ` of ${rowsTotal.toLocaleString()}` : ''} rows
          </span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr style={{ background: '#111827', color: '#fff' }}>
              {['Address', 'State', 'Broker', 'List Price', 'AMV', 'AMV Src', 'MLS Status', 'Deal?', 'Status'].map((h, i) => (
                <th
                  key={h}
                  style={{
                    textAlign: i === 0 ? 'left' : 'right',
                    padding: '12px 14px',
                    fontSize: 12,
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    fontWeight: 600,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowsLoading && (
              <tr>
                <td colSpan={9} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                  <CircularProgress size={18} sx={{ mr: 1 }} /> Loading...
                </td>
              </tr>
            )}
            {!rowsLoading && rows.length === 0 && (
              <tr>
                <td colSpan={9} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                  {viewMode === 'all' && 'No properties yet. The first auto-refresh runs ~4 minutes after server start.'}
                  {viewMode === 'with_amv' && 'No properties with AMV yet.'}
                  {viewMode === 'bofa_queue' && 'BofA queue is empty.'}
                  {viewMode === 'redfin_queue' && 'Redfin queue is empty — nothing waiting for Redfin fallback.'}
                  {viewMode === 'without_amv' && 'Nothing here — every property got an AMV from BofA or Redfin.'}
                  {viewMode === 'already_exists' && 'No duplicate properties detected.'}
                  {viewMode === 'skipped' && 'Nothing skipped — all qualifying properties were pushed.'}
                </td>
              </tr>
            )}
            {!rowsLoading && rows.map((p, i) => {
              const zebra = i % 2 === 0 ? '#ffffff' : '#f9fafb';
              const bg = p.isDeal ? '#f0fdf4' : zebra;
              const amv = p.joinedAmv || p.bofaAmv || 0;
              const hasAmv = amv > 0;
              const addressText = p.fullAddress || p.address || '—';
              return (
                <tr key={p._id} style={{ background: bg }}>
                  <td style={{ padding: 14, minWidth: 280, color: '#111', fontWeight: 600 }}>
                    {p.detailUrl ? (
                      <a href={p.detailUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 600 }}>
                        {addressText}
                      </a>
                    ) : addressText}
                  </td>
                  <td style={{ padding: 14, textAlign: 'right', color: '#6b7280' }}>{p.state || '—'}</td>
                  <td style={{ padding: 14, textAlign: 'right', color: '#6b7280', fontSize: 12 }}>{p.brokerId || '—'}</td>
                  <td style={{ padding: 14, textAlign: 'right', color: '#059669', fontWeight: 600 }}>{fmt(p.listingPrice)}</td>
                  <td style={{ padding: 14, textAlign: 'right', color: hasAmv ? '#7c3aed' : '#9ca3af', fontWeight: 600 }}>
                    {hasAmv ? fmt(amv) : '—'}
                  </td>
                  <td style={{ padding: 14, textAlign: 'right' }}>
                    {p.amvSource ? (
                      <Chip
                        label={p.amvSource}
                        size="small"
                        sx={{
                          fontSize: 10, fontWeight: 600,
                          bgcolor: p.amvSource === 'bofa' ? '#ede9fe' : p.amvSource === 'redfin' ? '#dcfce7' : '#dbeafe',
                          color: p.amvSource === 'bofa' ? '#6d28d9' : p.amvSource === 'redfin' ? '#166534' : '#1d4ed8',
                        }}
                      />
                    ) : (<span style={{ color: '#9ca3af' }}>—</span>)}
                  </td>
                  <td style={{ padding: 14, textAlign: 'right' }}>
                    {p.mlsStatus ? (
                      <Chip
                        label={p.mlsStatus}
                        size="small"
                        sx={{
                          fontSize: 10, fontWeight: 600,
                          bgcolor: p.mlsStatus === 'Active' ? '#dcfce7' : p.mlsStatus === 'Pending' ? '#fef3c7' : p.mlsStatus === 'Closed' ? '#f3f4f6' : '#dbeafe',
                          color: p.mlsStatus === 'Active' ? '#166534' : p.mlsStatus === 'Pending' ? '#92400e' : p.mlsStatus === 'Closed' ? '#6b7280' : '#1d4ed8',
                        }}
                      />
                    ) : (<span style={{ color: '#9ca3af' }}>—</span>)}
                  </td>
                  <td style={{ padding: 14, textAlign: 'right' }}>
                    {p.isDeal ? (
                      <Chip label="DEAL" size="small" sx={{ bgcolor: '#22c55e', color: '#fff', fontWeight: 600, fontSize: 10 }} />
                    ) : (<span style={{ color: '#9ca3af' }}>—</span>)}
                  </td>
                  <td style={{ padding: 14, textAlign: 'right' }}>
                    {p.pushStatus === 'already_exists' ? (
                      <Chip label={p.existingSource ? `exists (${p.existingSource})` : 'exists'} size="small"
                        sx={{ bgcolor: '#f3f4f6', color: '#6b7280', fontWeight: 600, fontSize: 10 }} />
                    ) : p.pushStatus === 'skipped_over_cap' ? (
                      <Chip label="over $500K" size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: 10 }} />
                    ) : p.pushStatus === 'skipped_no_price' ? (
                      <Chip label="no price" size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: 10 }} />
                    ) : p.pushStatus === 'skipped_not_single_family' ? (
                      <Chip label={p.propertyTypeLabel || 'not SF/Multi'} size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: 10 }} />
                    ) : p.pushStatus === 'skipped_status' ? (
                      <Chip label={`status: ${p.mlsStatus || '?'}`} size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: 10 }} />
                    ) : (
                      <Chip
                        label={p.bofaStatus || p.dealStage || (hasAmv ? 'done' : 'pending')}
                        size="small"
                        sx={{
                          fontSize: 10, fontWeight: 600,
                          bgcolor:
                            p.bofaStatus === 'success' ? '#dcfce7' :
                            p.bofaStatus === 'no_data' ? '#fef3c7' :
                            p.bofaStatus === 'failed' ? '#fee2e2' :
                            p.dealStage === 'bofa_amv_pending' ? '#dbeafe' : '#f3f4f6',
                          color:
                            p.bofaStatus === 'success' ? '#166534' :
                            p.bofaStatus === 'no_data' ? '#92400e' :
                            p.bofaStatus === 'failed' ? '#991b1b' :
                            p.dealStage === 'bofa_amv_pending' ? '#1d4ed8' : '#6b7280',
                        }}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, padding: '12px 0', borderTop: '1px solid #e5e7eb' }}>
            <Button size="small" disabled={page <= 1 || rowsLoading} onClick={() => setPage(1)} sx={{ textTransform: 'none', minWidth: 50 }}>First</Button>
            <Button size="small" disabled={page <= 1 || rowsLoading} onClick={() => setPage(p => Math.max(1, p - 1))} sx={{ textTransform: 'none', minWidth: 50 }}>Prev</Button>
            <span style={{ fontSize: 13, color: '#374151', fontWeight: 600, minWidth: 120, textAlign: 'center' }}>
              Page {page.toLocaleString()} of {totalPages.toLocaleString()} · {rowsTotal.toLocaleString()} total
            </span>
            <Button size="small" disabled={page >= totalPages || rowsLoading} onClick={() => setPage(p => Math.min(totalPages, p + 1))} sx={{ textTransform: 'none', minWidth: 50 }}>Next</Button>
            <Button size="small" disabled={page >= totalPages || rowsLoading} onClick={() => setPage(totalPages)} sx={{ textTransform: 'none', minWidth: 50 }}>Last</Button>
          </div>
        )}
      </div>
    </Box>
  );
}
